import axios from "axios";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { imagesUrls, twitterUsernameRegex } from "../../../constants";
import CommonButton from "../../components/CommonButton";
import CommonNavBar from "../../components/CommonNavBar";
import CommonPageTitle from "../../components/CommonPageTitle";
import PageView from "../../components/PageView";
import useThemeHook from "../../hooks/useThemeHook";
import { useTranslationHook } from "../../hooks/useTranslationHook";
import { THEME_COLORS } from "../../themes/DarkLight";
import arthereumProvider from "../../../backend/ArthereumProvider";
import Text from "../../components/ThemedText";
import { TRANSACTIONFLOW } from "../../types/types";
import { actions } from "../../redux";
import LoadingIndicator from "../../components/LoadingIndicator";
import { useLocation } from "react-router-dom";
import emitter, { EmitterEvents } from "../../utils/eventsEmitter";
import { AlertCircleOutline } from "react-ionicons";

interface Crypto {
  image: string;
  price_change_percentage_24h: number;
  current_price: number;
  name: string;
}

const initialCryptos: Crypto[] = [
  // {
  //   image: imagesUrls["arthereum"],
  //   price_change_percentage_24h: 0,
  //   current_price: 0.001,
  //   name: "Arthereum",
  // },
  {
    image: imagesUrls["pi"],
    price_change_percentage_24h: 0,
    current_price: 1,
    name: "PiCoin",
  },
];

const SendCryptoPage: React.FC = () => {
  const getTranslationText = useTranslationHook();
  const userData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;
  const location = useLocation();
  const locationState = location.state as any;

  const [cryptos, setCryptos] = useState<Crypto[]>(initialCryptos);
  const [username, setUsername] = useState<string>(
    locationState?.userToSend?.username
  );
  const [selectedCrypto, setSelectedCrypto] = useState<Crypto | null>(
    initialCryptos[0]
  );
  const [amount, setAmount] = useState<number>(0);
  const [warningText, setWarningText] = useState<string>("");
  const theme = useThemeHook();

  useEffect(() => {
    const fetchCryptoPrices = async () => {
      try {
        // const response = await axios.get(
        //   "https://api.coingecko.com/api/v3/coins/markets",
        //   {
        //     params: {
        //       vs_currency: "usd",
        //       ids: "bitcoin,ethereum,tether",
        //     },
        //   }
        // );

        // const fetchedCryptos = response.data.map((crypto: any) => ({
        //   image: crypto.image,
        //   price_change_percentage_24h: crypto.price_change_percentage_24h,
        //   current_price: crypto.current_price,
        //   name: crypto.name,
        // }));
        const fetchedCryptos: any[] = [];
        const stateCryptoName = location.state?.crypto?.name;
        const initialInputCrypto =
          fetchedCryptos.find(
            (crypto: any) => crypto.name === stateCryptoName
          ) ||
          initialCryptos.find(
            (crypto: any) => crypto.name === stateCryptoName
          ) ||
          fetchedCryptos[0];
        setCryptos([...initialCryptos, ...fetchedCryptos]);
        setSelectedCrypto(initialInputCrypto ?? initialCryptos[0]);
      } catch (error) {
        console.error("Error fetching crypto prices:", error);
      }
    };

    fetchCryptoPrices();
  }, []);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
  };

  const handleCryptoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = cryptos.find((c) => c.name === e.target.value);
    setSelectedCrypto(selected || null);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setAmount(value);

    if (selectedCrypto) {
      const balanceKey: any =
        selectedCrypto.name?.trim()?.toLowerCase() + "Balance";
      const availableBalance = user[balanceKey] || 0;

      if (value > availableBalance) {
        setWarningText(
          `Insufficient balance. Available: ${availableBalance.toFixed(6)} ${
            selectedCrypto.name
          }`
        );
      } else {
        setWarningText("");
      }
    }
  };
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const handleSend = async () => {
    if (isLoading) {
      return;
    }
    if (!username || !selectedCrypto || amount <= 0) {
      alert("Please fill in all fields.");
      return;
    }
    if (selectedCrypto?.name?.trim()?.toLowerCase() !== "picoin") {
      alert(getTranslationText("only_pi"));
      return;
    }

    if (warningText !== "") {
      emitter.emit(EmitterEvents.BOTTOM_SHEET_DISPLAY, {
        modal: {
          name: "alertModal",
          modalData: {
            customButton: {
              title: getTranslationText("deposit"),
              id: "deposit",
              bgColor: THEME_COLORS[theme].piPurple,
            },
            description: getTranslationText("insufficient_balance_desc", {
              crypto: "PiCoin",
            }),
            title: getTranslationText("insufficient_balance", {
              crypto: "PiCoin",
            }),
            icon: (
              <AlertCircleOutline
                color={THEME_COLORS[theme].red}
                height={"50px"}
                width={"50px"}
              />
            ),
          },
        },
      });
      return;
    }
    setIsLoading(true);
    if (await checkIfUsernameIsAvailable()) {
      alert(getTranslationText("username_not_found"));
      return;
    }
    try {
      const userNewData = await arthereumProvider.processTransaction({
        from: user?.id,
        toUsername: username,
        amount,
        crypto: selectedCrypto?.name,
      });
      dispatch(actions.setActualUser(userNewData));
      alert(getTranslationText("crypto_send_success", { username, amount }));
    } catch (error) {
      alert(getTranslationText("error"));
    } finally {
      setIsLoading(false);
    }
  };
  const [usernameAvailable, setUsernameAvailable] = useState(
    username?.length > 2 ? false : true
  );
  const checkIfUsernameIsAvailable = async () => {
    try {
      if (
        !twitterUsernameRegex.test(username?.trim()) &&
        username?.length >= 1
      ) {
        setUsernameAvailable(false);
      }
      const test = await arthereumProvider.checkIfUsernameIsAvailable(username);
      setUsernameAvailable(test.available);
      setUserTosend(test.user);
      return test;
    } catch (error) {
      setUsernameAvailable(false);
      return false;
    }
  };
  const [userTosend, setUserTosend] = useState(locationState?.userToSend);
  console.log("userTosend", userTosend);

  useEffect(() => {
    checkIfUsernameIsAvailable();
  }, [username]);

  return (
    <PageView>
      <CommonNavBar />
      <CommonPageTitle
        title={getTranslationText("send_crypto")}
        padding={null}
      />
      <div
        style={{
          width: "100%",
          padding: "10px",
          backgroundColor: THEME_COLORS[theme].component_background_color,
          borderRadius: "10px",
          boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={
              (userTosend as any)?.profileImage ??
              "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_960_720.png"
            }
            style={{
              objectFit: "cover",
              height: "100px",
              width: "100px",
              borderRadius: "10px",
              backgroundColor: THEME_COLORS[theme].less_theme,
            }}
            alt=""
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                color: THEME_COLORS[theme].inverse_theme,
              }}
            >
              {getTranslationText("username")}
            </label>
            <input
              type="text"
              value={username}
              onChange={handleUsernameChange}
              style={{
                width: "100%",
                padding: "10px",
                backgroundColor: THEME_COLORS[theme].less_theme,
                color: THEME_COLORS[theme].inverse_theme,
                border: "1px solid #ccc",
                borderRadius: "5px",
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flex: 1,
              }}
            >
              <img
                src={selectedCrypto?.image || ""}
                alt={selectedCrypto?.name || "Select Crypto"}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  backgroundColor: THEME_COLORS[theme].less_theme,
                }}
              />
              <select
                value={selectedCrypto?.name || ""}
                onChange={handleCryptoChange}
                style={{
                  flex: 1,
                  padding: "10px",
                  backgroundColor:
                    THEME_COLORS[theme].component_background_color,
                  color: THEME_COLORS[theme].inverse_theme,
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                }}
              >
                {cryptos.map((crypto) => (
                  <option key={crypto.name} value={crypto.name}>
                    {crypto.name}
                  </option>
                ))}
              </select>
            </div>
            <input
              type="number"
              value={amount}
              onChange={handleAmountChange}
              style={{
                width: "100%",
                padding: "10px",
                backgroundColor: THEME_COLORS[theme].less_theme,
                color: THEME_COLORS[theme].inverse_theme,
                border: "1px solid #ccc",
                borderRadius: "5px",
              }}
            />
          </div>
          {usernameAvailable && (
            <Text
              text={getTranslationText("username_not_found")}
              style={{ color: THEME_COLORS[theme].red }}
            />
          )}
          {isLoading && <LoadingIndicator />}
          <CommonButton
            onClickHandle={handleSend}
            text={getTranslationText("send")}
          />
        </div>
      </div>
      {warningText && (
        <p style={{ color: "red", margin: "0 10px" }}>{warningText}</p>
      )}
    </PageView>
  );
};

export default SendCryptoPage;
